import { type AxiosResponse } from "axios"
import { type IBaseRepository } from "@/interfaces/repository/base-repository.interface"
import { HTTPMethods, HTTPRequestBuilder } from "@/builder/HTTPRequestBuilder"
import { type IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface"
import type { IBase } from "@/interfaces/api/base.interface"
import DataHelper from "@/helper/data-helper"
import { ODataQueryBuilder } from "@/builder/odata-query-builder.ts"
import type { HTTPQuery } from "@/builder/http-query.ts"

export class BaseRepository<T extends IBase> implements IBaseRepository<T> {
  protected resourcePath!: string

  public async get(id: string, config: IRepositoryRequestConfig = {}): Promise<AxiosResponse<T>> {
    return await new HTTPRequestBuilder(HTTPMethods.GET, this.getResourcePath(id, config))
      .setbeforeErrorHandlingCallback(config.beforeErrorHandlingCallback)
      .addStatusCodeHandlers(config.statusCodeHandlers)
      .build()
      .execute()
  }

  /**
   * Fetches a list of items from the repository.
   * This method checks if the query exceeds the URL length limit. If it does, it uses a POST request to send the query
   * in the body.
   * @param config - Configuration options for the request, including query parameters and error handling callbacks.
   */
  public async getList(config: IRepositoryRequestConfig = {}): Promise<AxiosResponse<T[]>> {
    const queryExceedsURLLimit = this.queryExceedsURLLimit(config.query)

    // Determine the HTTP method based on whether the query exceeds the URL limit.
    const httpMethod = queryExceedsURLLimit ? HTTPMethods.POST : HTTPMethods.GET

    const httpRequestBuilder = new HTTPRequestBuilder(
      httpMethod,
      this.getResourcePath(undefined, config)
    )
      .setbeforeErrorHandlingCallback(config.beforeErrorHandlingCallback)
      .addStatusCodeHandlers(config.statusCodeHandlers)

    if (queryExceedsURLLimit) {
      // If the query exceeds the URL limit, we use POST to send the query in the body.
      // First convert the query to a string if it's not already.
      const query = HTTPRequestBuilder.toQueryString(config.query)
      httpRequestBuilder.setData({ query }) // Send the query in the body.
      httpRequestBuilder.query({ action: "list" }) // Indicate that this is a list action.
    } else {
      // If the query does not exceed the URL limit, we can use GET.
      httpRequestBuilder.query(config.query)
    }

    // Perform the request and denormalize the data.
    const response = await httpRequestBuilder.build().execute()
    if (response.data.results) {
      // Dealing with paginated data.
      // TODO, support routing pagination metadata back to the caller.
      response.data = response.data.results // Discard pagination metadata for now.
    }
    response.data = DataHelper.denormalizeData(response.data)

    return response
  }

  private queryExceedsURLLimit(
    query: Record<string, string | number | boolean> | ODataQueryBuilder | HTTPQuery | undefined
  ): boolean {
    const queryString = HTTPRequestBuilder.toQueryString(query)
    const urlLength = this.getResourcePath(undefined, {}).length + queryString.length
    // The maximum URL length is typically around 2000 characters, but can vary by browser and server.
    return urlLength > 2000
  }

  public async create(item: T, config: IRepositoryRequestConfig = {}): Promise<AxiosResponse<T>> {
    return await new HTTPRequestBuilder(HTTPMethods.POST, this.getResourcePath(undefined, config))
      .setData(item)
      .query(config.query)
      .setExpectedStatusCode(201)
      .setbeforeErrorHandlingCallback(config.beforeErrorHandlingCallback)
      .addStatusCodeHandlers(config.statusCodeHandlers)
      .build()
      .execute()
  }

  public async bulkCreate(
    items: T[],
    config: IRepositoryRequestConfig = {}
  ): Promise<AxiosResponse<T[]>> {
    const response = await new HTTPRequestBuilder(
      HTTPMethods.POST,
      this.getResourcePath(undefined, config, "create")
    )
      .setData(items)
      .query(config.query)
      .setExpectedStatusCode(201)
      .setbeforeErrorHandlingCallback(config.beforeErrorHandlingCallback)
      .addStatusCodeHandlers(config.statusCodeHandlers)
      .build()
      .execute()

    // Denormalize array of items
    response.data = DataHelper.denormalizeData(response.data)
    return response
  }

  public async update(
    id: string,
    item: T,
    config: IRepositoryRequestConfig = {}
  ): Promise<AxiosResponse<T>> {
    return await new HTTPRequestBuilder(HTTPMethods.PUT, this.getResourcePath(id, config))
      .setData(item)
      .query(config.query)
      .setbeforeErrorHandlingCallback(config.beforeErrorHandlingCallback)
      .addStatusCodeHandlers(config.statusCodeHandlers)
      .build()
      .execute()
  }

  public async bulkUpdate(
    items: T[],
    config: IRepositoryRequestConfig = {}
  ): Promise<AxiosResponse<T[]>> {
    return await new HTTPRequestBuilder(
      HTTPMethods.PUT,
      this.getResourcePath(undefined, config, "update")
    )
      .setData(items)
      .query(config.query)
      .setbeforeErrorHandlingCallback(config.beforeErrorHandlingCallback)
      .addStatusCodeHandlers(config.statusCodeHandlers)
      .build()
      .execute()
  }

  public async delete(
    id: string,
    config: IRepositoryRequestConfig = {}
  ): Promise<AxiosResponse<T>> {
    return await new HTTPRequestBuilder(HTTPMethods.DELETE, this.getResourcePath(id, config))
      .setExpectedStatusCode(204)
      .query(config.query)
      .setbeforeErrorHandlingCallback(config.beforeErrorHandlingCallback)
      .addStatusCodeHandlers(config.statusCodeHandlers)
      .build()
      .execute()
  }

  public async bulkDelete(
    ids: string[],
    config: IRepositoryRequestConfig = {}
  ): Promise<AxiosResponse<T[]>> {
    return await new HTTPRequestBuilder(
      HTTPMethods.POST, // Use POST because DELETE bodies are problematic
      this.getResourcePath(undefined, config, "delete")
    )
      .setData(ids) // send array of IDs to delete
      .query(config.query)
      .setExpectedStatusCode(204)
      .setbeforeErrorHandlingCallback(config.beforeErrorHandlingCallback)
      .addStatusCodeHandlers(config.statusCodeHandlers)
      .build()
      .execute()
  }

  protected getResourcePath(
    id?: string,
    config: IRepositoryRequestConfig = {},
    bulkActionType: "create" | "update" | "delete" | null = null
  ): string {
    let resourcePath = config.resourcePath || this.resourcePath
    resourcePath += config.resourcePathSuffix || ""

    if (bulkActionType) {
      if (id) {
        throw new Error("Cannot use 'id' parameter when bulk = true")
      }
      return `${resourcePath}/bulk-${bulkActionType}/`
    }

    return id ? `${resourcePath}/${id}/` : `${resourcePath}/`
  }
}
